import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { Box, List, Typography, Pagination } from '@mui/material';
import { colors } from '@src/styles';
import { staggerChildrenSX } from '@src/styles/animations';
import { TTaskItem } from '../hooks/dto/taskItem.dto';
import { TaskListHeader } from './TaskListHeader';
import { TaskRow } from './TaskRow';

type Order = 'asc' | 'desc';

const TABLE_MIN_WIDTH = 880;

interface TaskTableProps {
  tasks: TTaskItem[];
  onSortChange?: (sortOrder: 'asc' | 'desc') => void;
  parentSortOrder?: 'asc' | 'desc';
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  newTasksCount?: number;
}

export const TaskTable = ({
  tasks,
  onSortChange,
  parentSortOrder = 'desc',
  currentPage,
  totalPages,
  onPageChange,
  newTasksCount,
}: TaskTableProps) => {
  const [visibleCount, setVisibleCount] = useState(20);
  const [order, setOrder] = useState<Order>(parentSortOrder);
  const [orderBy, setOrderBy] = useState<keyof TTaskItem>('createdAt');
  const scrollRef = useRef<HTMLDivElement>(null);
  const previousTasksLengthRef = useRef<number>(tasks.length);
  const previousScrollTopRef = useRef<number>(0);
  const previousScrollHeightRef = useRef<number>(0);
  const previousNewTasksCountRef = useRef<number>(newTasksCount || 0);

  const isPaginated = currentPage !== undefined && totalPages !== undefined && onPageChange !== undefined;

  useEffect(() => {
    setOrder(parentSortOrder);
  }, [parentSortOrder]);

  useEffect(() => {
    setVisibleCount(20);
  }, [currentPage, tasks.length]);

  const handleSort = useCallback(
    (property: keyof TTaskItem) => {
      const isAsc = orderBy === property && order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      setOrder(newOrder);
      setOrderBy(property);

      if (property === 'createdAt' && onSortChange) {
        onSortChange(newOrder);
      }
    },
    [order, orderBy, onSortChange],
  );

  const processedTasks = useMemo(() => {
    const filtered = [...tasks];

    if (!isPaginated) {
      filtered.sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (orderBy === 'createdAt') {
          const aDate = new Date(aValue as string).getTime();
          const bDate = new Date(bValue as string).getTime();
          return order === 'asc' ? aDate - bDate : bDate - aDate;
        }

        if (bValue < aValue) return order === 'asc' ? 1 : -1;
        if (bValue > aValue) return order === 'asc' ? -1 : 1;
        return 0;
      });
    }

    return filtered;
  }, [tasks, order, orderBy, isPaginated]);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      previousScrollTopRef.current = scrollTop;
      previousScrollHeightRef.current = scrollHeight;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setVisibleCount((prevCount) => Math.min(prevCount + 20, processedTasks.length));
      }
    }
  }, [processedTasks.length]);

  useEffect(() => {
    const scrollableElement = scrollRef.current;
    if (scrollableElement) {
      scrollableElement.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollableElement) {
        scrollableElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  useLayoutEffect(() => {
    if (!scrollRef.current) return;

    const currentTasksLength = tasks.length;
    const previousTasksLength = previousTasksLengthRef.current;
    const currentNewTasksCount = newTasksCount || 0;
    const previousNewTasksCount = previousNewTasksCountRef.current;

    if (previousNewTasksCount > 0 && currentNewTasksCount === 0 && currentTasksLength > previousTasksLength) {
      const currentScrollHeight = scrollRef.current.scrollHeight;
      const previousScrollHeight = previousScrollHeightRef.current;

      if (previousScrollHeight > 0 && previousScrollTopRef.current > 0) {
        const heightDifference = currentScrollHeight - previousScrollHeight;
        scrollRef.current.scrollTop = previousScrollTopRef.current + heightDifference;
      }
    }

    previousTasksLengthRef.current = currentTasksLength;
    previousScrollHeightRef.current = scrollRef.current.scrollHeight;
    previousScrollTopRef.current = scrollRef.current.scrollTop;
    previousNewTasksCountRef.current = currentNewTasksCount;
  }, [tasks.length, newTasksCount]);

  return (
    <Box
      sx={{
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowX: 'auto', display: 'flex' }}>
        <Box sx={{ flex: 1, minWidth: TABLE_MIN_WIDTH, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <TaskListHeader order={order} orderBy={orderBy} handleSort={handleSort} />
          <Box
            ref={scrollRef}
            sx={{
              overflowY: 'auto',
              flexGrow: 1,
              minHeight: 0,
              padding: '4px 4px 12px',
            }}
          >
            <List sx={{ padding: 0, ...staggerChildrenSX('.MuiListItem-root') }}>
              {processedTasks.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 4,
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: colors.primary.white, opacity: 0.6, textAlign: 'center' }}>
                    Nenhuma tarefa encontrada
                  </Typography>
                </Box>
              ) : (
                processedTasks.slice(0, visibleCount).map((task) => <TaskRow key={task.id} task={task} />)
              )}
            </List>
          </Box>
        </Box>
      </Box>
      {isPaginated && totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px 12px 4px',
            borderTop: `1px solid ${colors.brand.line}`,
            backgroundColor: colors.background.primary,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                color: colors.primary.white,
                borderColor: colors.background.secondary,
                '&:hover': { backgroundColor: colors.background.secondary },
              },
              '& .Mui-selected': {
                backgroundColor: colors.primary.yellow,
                color: colors.background.primary,
                '&:hover': { backgroundColor: colors.primary.yellow },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
